import { BreadcrumbComponent, Button, CIActionGuard, CIAppliedFilter, CIAppliedListFilter, CIPageHeader, SearchInput, toLocalDate, useToastMessage, useUserPermission } from 'ccs-ui-components';
import debounce from 'lodash.debounce';
import { useCallback, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { requiredAuthPermission } from '../../../../../common/constants/UserPermissionConstants';
import Filter, { statusList } from '../../../../_components/filter/Filter';
import { EVENT_ASSOCIATION_TABS, EVENT_LIST_WRAPPER_STR, EVENT_ROUTE, SORT_STRINGS, allStatusList } from '../../../../constants';
import { ButtonProp, IFilterData, IQueryParams, IRecordModel } from '../../../../models';
import { AppDispatch, useSelector } from '../../../../store/AppStore';
import useExportEventConfiguration from '../../../../store/_api/events/ExportEventConfiguration';
import { clearEvents } from '../../../../store/actions/sagaActions/events/CreateEventSagaActions';
import { clearAllEvent, fetchEventListRequest } from '../../../../store/actions/sagaActions/events/GetAllEventSagaActions';
import { addClearData, addFilterEventData, clearFilterEvent } from '../../../../store/reducer/events/EventFilterReducer';
import { clearImportEvent } from '../../../../store/reducer/events/ImportEventReducer';
import { arrayBufferToString, exportConfigConst } from '../../../../utilities/services/ServiceListUtils';
import { EventList } from '../list-event/EventList';

import WarningMessage from '../../../../_components/warning-message/WarningMessage';
import { DEPENDENCY_ACTIONS, dependencyGraphInitialState, getDependencyGraphStateReducer } from '../../../../utilities/RegistryDependancyManager';
import classes from './EventsListWrapper.module.scss';
import { defaultEventListStatus, defaultEventListStatusArray } from '../../../../utilities/events/EventsListUtils';

const EventsListWrapper = () => {
  // 3rd party initialiZation
  const dispatch = useDispatch<AppDispatch>();
  const history = useNavigate();
  const route = useLocation();
  const { t: translate } = useTranslation();
  const [dependencyState, dispatchDependencyState] = useReducer(getDependencyGraphStateReducer, dependencyGraphInitialState);

  // hooks
  const { permissionObject } = useUserPermission();
  const { success, failure, renderToast } = useToastMessage();

  // Get store data
  const { events } = useSelector((state) => state.fetchAllEventReducer);
  const { searchText, status, fromDate, toDate, sort } = useSelector((state) => state.filterEventReducer);
  const { events: listEvents } = useSelector((state) => state.fetchAllEventReducer);
  const removeQuestionMark = route.search.substring(route.search.indexOf('?') + 1);
  let params: IFilterData = {
    status: '',
    eventName: '',
    fromDate: '',
    toDate: '',
    sort: '',
    type: ''
  };
  try {
    params = JSON.parse(`{"${decodeURIComponent(removeQuestionMark.replace(/&/g, '","').replace(/=/g, '":"'))}"}`);
  } catch { /* empty */ }
  const sortParamCondition = params.sort !== '' ? `${params.sort},${params.type}` : SORT_STRINGS.LAST_MODIFIED;
  const filterData = {
    searchText: searchText !== '' ? searchText : params.eventName,
    status: status !== '' ? status : params?.status || statusList.toString(),
    fromDate: fromDate !== null ? fromDate?.toString() : params.fromDate,
    toDate: toDate !== null ? toDate?.toString() : params.toDate,
    pageNo: events.currentPageNumber,
    item: events.numberOfRecordsPerPage,
    searchTitle: 'event_list_component.lbl_event_name_search',
    sort: sort !== '' ? sort : sortParamCondition
  };

  // states
  const [selectedEventList, setSelectedEventList] = useState<IRecordModel[]>([]);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [exportSuccess, setExportSuccess] = useState<boolean>(false);
  const [searchTemp, setSearchTemp] = useState<string>(params.eventName || searchText || '');

  const { exportEventConfiguration } = useExportEventConfiguration();

  /**
   * @method selectedExportItem
   * @description Updates the selected event list and sets the export success flag.
   * @param item - The array of IRecordModel objects representing the selected events.
   */
  const selectedExportItem = (item: IRecordModel[]) => {
    setSelectedEventList(item);
    setExportSuccess(false);
  };

  /**
   * @method exportEventHandler
   * @description Export event handler function.
   * This function is responsible for exporting event configurations.
   * It sets the export loading state, sends a request to export the event configuration,
   * creates a download link for the exported file, and displays success or failure toast messages.
   */
  const exportEventHandler = async () => {
    try {
      setExportLoading(true);
      setExportSuccess(false);
      const resp = await exportEventConfiguration(selectedEventList);
      if (resp.data) {
        const contentDisposition = resp.request.getResponseHeader(exportConfigConst.contentDisposition_text);
        const filename = contentDisposition ? contentDisposition.split(EVENT_LIST_WRAPPER_STR.SPLIT_STR)[1].split('.')[0] : 'download';
        const url = window.URL.createObjectURL(
          new Blob([resp.data], { type: EVENT_LIST_WRAPPER_STR.FILE_TYPE })
        );
        // Create a temporary download link
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(exportConfigConst.download_text, `${filename}${EVENT_LIST_WRAPPER_STR.ZIP_EXT}`);
        // Simulate a click event
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        link.dispatchEvent(clickEvent);

        // Clean up the URL object
        window.URL.revokeObjectURL(url);

        success(
          translate('toast_component.lbl_success'),
          translate('events_wrapper.msg_export_evnt_config_success_body')
        );
        setExportSuccess(true);
        setExportLoading(false);
      }
       
    } catch (error: any) {
      const responseData = error?.response?.data;
      let data;
      if (error?.response?.data) {
        data = await JSON.parse(arrayBufferToString(responseData));
      }
      setExportLoading(false);
      failure(
        translate('toast_component.lbl_error'),
        data?.errorMessage
      );
    }
  };

  /**
   * @method handleCreateClick
   * @description Handles the click event for the create new event button.
   * Clears the filter event, clears all events, and navigates to the create event route.
   */
  const handleCreateClick = () => {
    dispatch(clearFilterEvent());
    dispatch(clearAllEvent());
    dispatch(clearEvents());
    history(EVENT_ROUTE.CREATE_EVENT_ROUTE, { state: { path: route.search, creatreNewEventButtonClicked: true } });
  };

  /**
   * @method handleNavigateToImport
   * @description Handles the navigation to the import page and clears the import event state.
   */
  const handleNavigateToImport = () => {
    history(EVENT_ROUTE.UPLOAD_FILES_ROUTE);
    dispatch(clearImportEvent());
  };

  const allStatusMap: { [key: string]: string } = {};
  allStatusList.forEach((s) => { allStatusMap[s.value] = s.label; });

  const debounceDelay = 300;

  /**
   * @method debounceHandler
   * @description Debounces the handler function and performs various dispatch actions based on the debounced value.
   * @param value - The value to be debounced.
   */
  const debounceHandler = useCallback(debounce((value: string) => {
    const queryParam = {
      searchText: value,
      status,
      toDate,
      fromDate,
    };
    dispatch(addFilterEventData(queryParam));
    dispatch(addClearData(ButtonProp.search));
    dispatch(fetchEventListRequest(0, filterData.item, queryParam, SORT_STRINGS.LAST_MODIFIED));
  }, debounceDelay), [searchTemp]);

  /**
   * @method searchHandler
   * @description Handles the search event and updates the search term state.
   * @param event - The change event triggered by the input element.
   */
  const searchHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTemp((event.target.value).trim());
    debounceHandler((event.target.value).trim());
  };

  /**
   * Returns the applied filter data based on the provided filter data.
   * @param filterData - The filter data object.
   * @returns An array of CIAppliedFilter objects representing the applied filters.
   */
  const appliedFilterData: CIAppliedFilter[] = useMemo(() => {
    const data: CIAppliedFilter[] = [];
    const filterStatusList: string[] = filterData?.status?.split(',') ?? [];
    if (!filterData) {
      return [];
    }
    if (filterStatusList?.length > 0 && filterStatusList?.length < allStatusList.length) {
      filterStatusList?.forEach((f: string) => data.push({ text: allStatusMap[f], type: EVENT_LIST_WRAPPER_STR.STATUS_STR, value: f }));
    }
    if (filterData.fromDate && filterData.toDate) {
      data.push({ value: filterData.toDate, type: EVENT_LIST_WRAPPER_STR.DATE_STR, text: `${EVENT_LIST_WRAPPER_STR.LAST_MODIFIED_STR} ${toLocalDate({ date: filterData.fromDate, dateTimeFormat: { date: true } })} - ${toLocalDate({ date: filterData.toDate, dateTimeFormat: { date: true } })}` });
    } else if (filterData.fromDate) {
      data.push({ value: filterData.fromDate, type: EVENT_LIST_WRAPPER_STR.START_DATE_STR, text: `${EVENT_LIST_WRAPPER_STR.LAST_MODIFIED_FROM} ${toLocalDate({ date: filterData.fromDate, dateTimeFormat: { date: true } })}` });
    } else if (filterData.toDate) {
      data.push({ value: filterData.toDate, type: EVENT_LIST_WRAPPER_STR.END_DATE, text: `${EVENT_LIST_WRAPPER_STR.LAST_MODIFIED_TO} ${toLocalDate({ date: filterData.toDate, dateTimeFormat: { date: true } })}` });
    }
    return data;
  }, [filterData]);

  const isShowingDefaultFilter = useMemo(
    () => {
      const filterStatusList: string[] = filterData?.status?.split(',') ?? [];
      const defaultSet = new Set(defaultEventListStatusArray);
      const isDefaultStatuses = filterStatusList.length === defaultEventListStatusArray.length && filterStatusList.every((s) => defaultSet.has(s));
      return isDefaultStatuses && !filterData?.fromDate && !filterData?.toDate;
    },
    [filterData]
  );

  /**
   * @method appliedFilterChangeHandler
   * @description Handles the change event for the applied filters.
   * @param filters - An array of CIAppliedFilter objects representing the applied filters.
   */
  const appliedFilterChangeHandler = (filters: CIAppliedFilter[]) => {
    const statusList: string[] = [];
    let hasStartDate = false;
    let hasEndDate = false;

    filters.forEach((f) => {
      if (f.type === EVENT_LIST_WRAPPER_STR.STATUS_STR) {
        statusList.push(f.value);
      } else if (f.type === EVENT_LIST_WRAPPER_STR.START_DATE_STR) {
        hasStartDate = true;
      } else if (f.type === EVENT_LIST_WRAPPER_STR.END_DATE) {
        hasEndDate = true;
      } else if (f.type === EVENT_LIST_WRAPPER_STR.DATE_STR) {
        hasStartDate = true;
        hasEndDate = true;
      }
    });

    const data = {
      startDate: hasStartDate ? filterData?.fromDate : undefined,
      endDate: hasEndDate ? filterData?.toDate : undefined
    };

    const queryParam: IQueryParams = {
      searchText: searchText !== '' ? searchText : searchTemp,
      status: statusList?.join(',') ?? '',
      fromDate: data.startDate ?? '',
      toDate: data.endDate ?? '',
    };

    dispatch(addFilterEventData(queryParam));
    dispatch(addClearData(ButtonProp.apply));
    dispatch(fetchEventListRequest(0, filterData.item, queryParam, sort));
  };

  const resetFiltersHandler = () => {
    const queryParam: IQueryParams = {
      searchText: searchText !== '' ? searchText : searchTemp,
      status: defaultEventListStatus,
      fromDate: '',
      toDate: '',
    };

    dispatch(addFilterEventData(queryParam));
    dispatch(addClearData(ButtonProp.clear));
    dispatch(fetchEventListRequest(0, filterData.item, queryParam, sort));
  };

  const onDependencyGraphFound = (isFound: boolean, registryName: string, entityVersion: string, entityStatus: string) => {
    dispatchDependencyState({ type: DEPENDENCY_ACTIONS.DEPENDENCY_GRAPH_FOUND, addDependencyGraphPayload: { registryName, entityVersion, entityStatus, isDependencyGraphFound: isFound } });
  };

  const onCloseWarningClick = () => {
    dispatchDependencyState({ type: DEPENDENCY_ACTIONS.CLEAR_DEPENDENCY_GRAPH });
  };

  const navigateToEventDependency = () => {
    history(
      `${EVENT_ROUTE.ASSOCIATIONS_ROUTE}${dependencyState.registryName}/${dependencyState.entityVersion}/${EVENT_ASSOCIATION_TABS.SOURCES}`,
      { state: { eventName: dependencyState.registryName, eventVersion: dependencyState.entityVersion, associationStatus: dependencyState.entityStatus } }
    );
  };

  const showDependencyGraphWarning = () => {
    if (dependencyState.isDependencyGraphFound) {
      return (
        <div className={classes['event-exist-warning']}>
          <div className={classes['warning-pad']} />
          <WarningMessage entityName={dependencyState.registryName} onHighlightFieldClick={navigateToEventDependency} onCloseWarningClick={onCloseWarningClick} />
        </div>
      );
    }
    return null;
  };

  return (
    <div className={classes['event-list-wrapper']}>
      <div className={classes['main-container']}>
        <div className={classes['header-container']}>
          <BreadcrumbComponent />
          {renderToast()}
          <span className={classes['list-title']}>
            <CIPageHeader header={translate('view_events_list.title')} />
            <div className={classes.filters}>
              <div className={classes['search-filter-div']}>
                <Filter
                  dispatcherFunction={fetchEventListRequest}
                  reducerFunction={addFilterEventData}
                  filterData={filterData}
                  clearFunction={clearFilterEvent}
                  clearAction={addClearData}
                />
                <div className={classes.search}>
                  <SearchInput
                    label="search"
                    type="search"
                    placeholder={String(translate('view_events_list.txt_search'))}
                    onChange={searchHandler}
                    data-testid="filterSearchInput"
                    value={searchTemp}
                  />
                </div>
                <div className={`${classes.buttons} ci-button-group`}>
                  {selectedEventList?.length ? (
                    <Button
                      onClick={exportEventHandler}
                      variant="secondary"
                      data-testid="exportEventConfiguration"
                    >
                      <i className="bi bi-download" /> {translate('view_events_list.btn_export_event_configuration')}
                    </Button>
                  ) : (CIActionGuard([requiredAuthPermission.Events.edit, requiredAuthPermission.Events.approve], permissionObject.permission) && (
                    <Button
                      variant="secondary"
                      onClick={handleNavigateToImport}
                      data-testid="importEventConfiguration"
                    >
                      {translate('view_events_list.btn_import_event_configuration')}
                    </Button>)
                  )}
                  {CIActionGuard([requiredAuthPermission.Events.edit, requiredAuthPermission.Events.approve], permissionObject.permission)
                    && (
                      <Button
                        variant="primary"
                        onClick={handleCreateClick}
                        className={classes['create-events-btn']}
                        data-testid="create-event-btn"
                      >
                        {translate('view_events_list.btn_create_new_event')}
                      </Button>
                    )}
                </div>

              </div>
              <CIAppliedListFilter
                className={classes['applied-filters']}
                totalRecords={listEvents.totalRecords}
                appliedFilters={appliedFilterData}
                onFilterChange={appliedFilterChangeHandler}
                onResetFilters={resetFiltersHandler}
                showResetFiltersButton={!isShowingDefaultFilter}
              />
            </div>
          </span>
        </div>
        <div>

          <div className={classes['list-container']}>
            {showDependencyGraphWarning()}
            <EventList selectedExportItem={selectedExportItem} exportLoading={exportLoading} exportSuccess={exportSuccess} onDependencyGraphFound={onDependencyGraphFound} />
          </div>
        </div>
      </div>
    </div>
  );
};
export default EventsListWrapper;
